import { useEffect, useRef, useState } from "react";
import cls from "classnames";
import { callMinaSidorApi, getSessionId } from "../Utils/api";
import styles from "./BankIDLoginModal.module.scss";
import QRCode from "qrcode";
import config from "../config";

type Props = {
  visible: boolean;
  onAuthenticated: (result: BankIdState) => void;
  onBankIdSessionCreated: (sessionId: string) => void;
};

type BankIdSession = {
  id: string;
  qrStartToken: string;
  autoStartToken: string;
}

type BankIdState = {
  status?: string;

  /**
   * Set if authenticated.
   */
  userToken?: string;
};

/**
 * Modal for logging in using BankID.
 */
export default function BankIDLoginModal({ onAuthenticated, onBankIdSessionCreated, visible }: Props) {
  const [isCompany, setIsCompany] = useState(config.showCompanyLoginByDefault);
  const [error, setError] = useState<any>(null);

  const [qrData, setQrData] = useState<string>(null);
  const [bankId, setBankId] = useState<BankIdSession | null>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [callbackId, setCallbackId] = useState(
    new URL(document.location.href).searchParams.get('bankId')
  );

  async function checkStatus(id: string) {
    try {
      const result = await callMinaSidorApi<BankIdState>('get', `/bankid/get_login_status.php?sessionId=${getSessionId()}&id=${id}`);

      if (result.status === "COMPLETED") {
        onAuthenticated(result);
        return true;
      }
    } catch (error) {
      console.error("Error checking BankID status", error);

      setError(error);
    }

    return false;
  }

  async function createBankIdSession() {
    try {
      const { id, qrStartToken, autoStartToken, sessionId } = await callMinaSidorApi<BankIdSession & { sessionId: string }>('post', '/bankid/log_in.php');

      if (qrStartToken && sessionId) {
        setBankId(
          {
            id,
            qrStartToken,
            autoStartToken
          }
        );

        onBankIdSessionCreated(sessionId);
      }
    } catch (error) {
      console.error("Error creating BankID session", error);

      setError(error);
    }
  }

  async function refreshQr(startToken: string, onRefresh: Function) {
    try {
      setQrData(
        await callMinaSidorApi<{ data: string }>('post', `/bankid/qr.php?sessionId=${getSessionId()}&startToken=${startToken}`)
          .then(({ data }) => data)
      );
    } catch (error) {
      console.error("Error refreshing QR data", error);

      if (error.code === "202404151025") {
        //  Token has expired, try again.
        onRefresh();
      }
    }
  }

  useEffect(() => {
    if (!bankId?.qrStartToken || !visible) {
      return;
    }

    refreshQr(bankId.qrStartToken, () => createBankIdSession());

    const refreshQrId = setInterval(() => {
      if (document.hidden) {
        return;
      }

      refreshQr(bankId.qrStartToken, () => {
        clearInterval(refreshQrId);
        createBankIdSession();
      });
    }, 1000);

    return () => { clearInterval(refreshQrId); }
  }, [bankId?.qrStartToken, visible]);

  useEffect(() => {
    if (!qrData) {
      return;
    }

    QRCode.toCanvas(qrCanvasRef.current, qrData)
      .catch(error => console.error("Error creating QR code", error));
  }, [qrData]);

  useEffect(() => {
    if (!bankId?.id || !visible) {
      return;
    }

    const checkId = setInterval(() => {
      if (document.hidden) {
        return;
      }

      checkStatus(bankId.id);
    }, 3e3);
    return () => { clearInterval(checkId); }
  }, [bankId?.id, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (callbackId) {
      checkStatus(callbackId)
        .then(ok => {
          if (!ok) {
            //  Skapa en ny session.
            setCallbackId(null);
          }
        })
    } else {
      createBankIdSession();
    }
  }, [visible, Boolean(callbackId)]);

  useEffect(() => {
    if (!callbackId) {
      return;
    }

    const id = setTimeout(() => {
      //  Ta bort query-parametern efter ett kort tag då vi sparat den som en ref.
      //  Vi kan inte ta bort den direkt för då riskerar vi att få en callbackId = null och
      //  då måste man autentisera sig igen.
      const url = new URL(document.location.href);
      url.searchParams.delete('bankId');
      window.history.pushState({}, '', url.href);
    }, 300);

    return () => { clearTimeout(id); }
  }, [Boolean(callbackId)]);

  useEffect(() => {
    //  Rensa felet - det kanske funkar att logga in i det andra läget.
    setError(null);

    //  Detta avgör hurvida vi gör ett uppslag i get_login_status eller inte.
    callMinaSidorApi('post', `/bankid/update_is_company.php?sessionId=${getSessionId()}`, { isCompany: Boolean(isCompany) })
  }, [Boolean(isCompany)]);

  return (
    <div
      className={cls({
        [styles.backdrop]: true,
        [styles.visible]: visible,
      })}
    >
      <div
        className={styles.modal}>
        <img
          style={{ width: "100%" }}
          src={config.loginLogoUrl}
          alt={config.companyName + " logotyp"}
        />
        <p className={styles.ingress}>
          {error
            ? "Ett fel inträffade. Prova igen eller kontakta vår kundservice."
            : "Logga in med BankID för att se din förbrukning, fakturor och mer."}
        </p>

        <canvas className={styles.qrCanvas} ref={element => qrCanvasRef.current = element}>
        </canvas>

        <p
          style={{
            fontSize: "12px",
            lineHeight: 1.6,
            background: "#f2f2f2",
            padding: 15,
            borderRadius: 20,
          }}
        >
          {
            isCompany ? (
              <div>
                Skanna QR-koden med BankID mobilapp.<br></br>Ditt personnummer behöver vara kopplat till ett företag. Inte
                kopplat ännu? Kontakta vår kundservice så hjälper vi dig.
              </div>
            ) : (
              <div>
                Skanna QR-koden med BankID mobilapp.
              </div>
            )
          }
        </p>

        <a
          style={{ color: "var(--color-primary)", fontSize: 14, cursor: "pointer" }}
          href={getAutoStartUrl(bankId?.autoStartToken, bankId?.id)}>
          Öppna BankID-appen
        </a>

        <p
          style={{ color: "var(--color-primary)", fontSize: 14, cursor: "pointer" }}
          onClick={() => {
            setIsCompany(!isCompany);
          }}
        >
          {!isCompany ? "Byt till Företagsinlogg" : "Byt till Privatinlogg"}
        </p>
      </div>
    </div>
  );
}

function getAutoStartUrl(autoStartToken: string, id: string) {
  const redirect = new URL(document.location.href);
  redirect.searchParams.set('bankId', id);

  return `https://app.bankid.com/?autostarttoken=${encodeURIComponent(autoStartToken)}&redirect=${encodeURIComponent(redirect.href)}`;
}